import React, { useEffect, useMemo, useState } from 'react';
import { UserProfile, Expert } from '../../../types';
import { Sun, Square, CheckSquare, RefreshCw, X, Code, Save, Loader2, Eye } from 'lucide-react';
import { ZODIAC_SIGNS } from '../../Horoscope/config';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';

interface Horoscope {
  id: number;
  sign: string;
  vertical: string;
  period: string;
  period_start: string;
  period_end: string;
  text: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  authors?: { id: number; name: string; avatar: string | null; slug?: string; style?: string }[];
}

interface Props {
  currentUser: UserProfile;
}

type AutoTab =
  | 'daily'
  | 'tomorrow'
  | 'weekly'
  | 'weekly_next'
  | 'monthly'
  | 'monthly_next'
  | 'yearly'
  | 'yearly_next';

const AutoUpdaterTab: React.FC<Props> = ({ currentUser }) => {
  const [activeAutoTab, setActiveAutoTab] = useState<AutoTab>('daily');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [records, setRecords] = useState<Record<AutoTab, Horoscope[]>>({
    daily: [],
    tomorrow: [],
    weekly: [],
    weekly_next: [],
    monthly: [],
    monthly_next: [],
    yearly: [],
    yearly_next: [],
  });
  const [loadedTabs, setLoadedTabs] = useState<Set<AutoTab>>(new Set());
  const [loading, setLoading] = useState(false);
  const [experts, setExperts] = useState<Expert[]>([]);
  const [expertsLoaded, setExpertsLoaded] = useState(false);
  const [editing, setEditing] = useState<Horoscope | null>(null);
  const [promptText, setPromptText] = useState('');
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [selectedExpertId, setSelectedExpertId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [promptPreview, setPromptPreview] = useState<string | null>(null);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchCurrentIndex, setBatchCurrentIndex] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchCurrentId, setBatchCurrentId] = useState<number | null>(null);
  const [generatedByRowId, setGeneratedByRowId] = useState<
    Record<number, { text: string; expertId: number | null }>
  >({});
  const [codePreviewRowId, setCodePreviewRowId] = useState<number | null>(null);
  const [htmlPreviewRowId, setHtmlPreviewRowId] = useState<number | null>(null);
  const [savingRowId, setSavingRowId] = useState<number | null>(null);

  const getTodayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const now = new Date();
  const todayStr = getTodayStr(); // 'YYYY-MM-DD' w lokalnym czasie

  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = [
    tomorrowDate.getFullYear(),
    String(tomorrowDate.getMonth() + 1).padStart(2, '0'),
    String(tomorrowDate.getDate()).padStart(2, '0'),
  ].join('-');

  const startOfWeek = new Date(now);
  const day = startOfWeek.getDay() || 7; // 1 (Mon) - 7 (Sun)
  if (day !== 1) {
    startOfWeek.setDate(startOfWeek.getDate() - (day - 1));
  }
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const nextWeekStart = new Date(startOfWeek);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);
  const nextWeekEnd = new Date(nextWeekStart);
  nextWeekEnd.setDate(nextWeekStart.getDate() + 6);

  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);

  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const endOfYear = new Date(now.getFullYear(), 11, 31);

  const nextYearStart = new Date(now.getFullYear() + 1, 0, 1);
  const nextYearEnd = new Date(now.getFullYear() + 1, 11, 31);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  /** Etykieta okresu horoskopu po polsku (wg pola period rekordu). */
  const periodKindPl = (period: string | undefined | null) => {
    const p = String(period || '').toLowerCase();
    if (p === 'daily') return 'dzienny';
    if (p === 'weekly') return 'tygodniowy';
    if (p === 'monthly') return 'miesięczny';
    if (p === 'yearly') return 'roczny';
    return p || 'nieokreślony';
  };

  /** Jedna data (dzienny) lub zakres dat (tygodniowy / miesięczny / roczny) z rekordu. */
  const getPeriodDateLineForPrompt = (h: Horoscope) => {
    const p = String(h.period || '').toLowerCase();
    const start = formatDate(h.period_start);
    const end = formatDate(h.period_end);
    const rangeStr = h.period_start && h.period_end && start !== end ? ` (${start} - ${end})` : '';
    if (p === 'daily') {
      return start;
    }
    if (p === 'weekly') {
      return rangeStr ? `${start} - ${end}` : start;
    }
    if (p === 'monthly' && h.period_start) {
      const d = new Date(h.period_start);
      const monthYear = d.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
      return rangeStr ? `${monthYear}${rangeStr}` : monthYear;
    }
    if (p === 'yearly' && h.period_start) {
      const year = new Date(h.period_start).getFullYear();
      return rangeStr ? `${year}${rangeStr}` : String(year);
    }
    return rangeStr ? `${start} - ${end}` : start;
  };

  const isDateInRange = (start: string | null, end: string | null, from: Date, to: Date) => {
    if (!start && !end) return false;
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;
    const fromTime = from.getTime();
    const toTime = to.getTime();
    const startTime = startDate ? startDate.getTime() : fromTime;
    const endTime = endDate ? endDate.getTime() : toTime;
    return endTime >= fromTime && startTime <= toTime;
  };

  const highlightDiffHtml = (oldHtml: string, newHtml: string) => {
    if (!newHtml) return '';
    const oldLines = oldHtml.split('\n');
    const newLines = newHtml.split('\n');
    const max = newLines.length;
    const out: string[] = [];
    for (let i = 0; i < max; i++) {
      const line = newLines[i] ?? '';
      const oldLine = oldLines[i] ?? '';
      if (!line.trim()) continue;
      if (line === oldLine) {
        out.push(line);
      } else {
        // prosty diff na poziomie słów w obrębie danej linii
        const oldWords = oldLine.split(/(\s+)/); // zachowujemy separatory
        const newWords = line.split(/(\s+)/);
        const len = newWords.length;
        const parts: string[] = [];
        for (let j = 0; j < len; j++) {
          const wNew = newWords[j];
          const wOld = oldWords[j];
          if (!wNew) continue;
          if (wNew === wOld) {
            parts.push(wNew);
          } else {
            parts.push(`<mark>${wNew}</mark>`);
          }
        }
        out.push(parts.join(''));
      }
    }
    return out.join('\n');
  };

  // Filtry okresow wg zakladki (ten sam okres co zapytanie /api/cms/horoscope-auto).

  const dailyRecords = useMemo(() => {
    return records.daily
      .filter((h) => {
        if (!h.period_start) return false;
        return h.period.toLowerCase() === 'daily' && h.period_start.slice(0, 10) === todayStr;
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.daily, todayStr]);

  const weeklyRecords = useMemo(() => {
    return records.weekly
      .filter((h) => {
        if (h.period.toLowerCase() !== 'weekly') return false;
        return isDateInRange(h.period_start, h.period_end, startOfWeek, endOfWeek);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.weekly, startOfWeek.getTime(), endOfWeek.getTime()]);

  const monthlyRecords = useMemo(() => {
    return records.monthly
      .filter((h) => {
        if (h.period.toLowerCase() !== 'monthly') return false;
        return isDateInRange(h.period_start, h.period_end, startOfMonth, endOfMonth);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.monthly, startOfMonth.getTime(), endOfMonth.getTime()]);

  const yearlyRecords = useMemo(() => {
    return records.yearly
      .filter((h) => {
        if (h.period.toLowerCase() !== 'yearly') return false;
        return isDateInRange(h.period_start, h.period_end, startOfYear, endOfYear);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.yearly, startOfYear.getTime(), endOfYear.getTime()]);

  const weeklyNextRecords = useMemo(() => {
    return records.weekly_next
      .filter((h) => {
        if (h.period.toLowerCase() !== 'weekly') return false;
        return isDateInRange(h.period_start, h.period_end, nextWeekStart, nextWeekEnd);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.weekly_next, nextWeekStart.getTime(), nextWeekEnd.getTime()]);

  const monthlyNextRecords = useMemo(() => {
    return records.monthly_next
      .filter((h) => {
        if (h.period.toLowerCase() !== 'monthly') return false;
        return isDateInRange(h.period_start, h.period_end, nextMonthStart, nextMonthEnd);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.monthly_next, nextMonthStart.getTime(), nextMonthEnd.getTime()]);

  const yearlyNextRecords = useMemo(() => {
    return records.yearly_next
      .filter((h) => {
        if (h.period.toLowerCase() !== 'yearly') return false;
        return isDateInRange(h.period_start, h.period_end, nextYearStart, nextYearEnd);
      })
      .sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.yearly_next, nextYearStart.getTime(), nextYearEnd.getTime()]);

  const tomorrowRecords = useMemo(() => {
    return [...records.tomorrow].sort((a, b) => a.sign.localeCompare(b.sign));
  }, [records.tomorrow]);

  const currentRecords = useMemo(() => {
    switch (activeAutoTab) {
      case 'daily':
        return dailyRecords;
      case 'tomorrow':
        return tomorrowRecords;
      case 'weekly':
        return weeklyRecords;
      case 'weekly_next':
        return weeklyNextRecords;
      case 'monthly':
        return monthlyRecords;
      case 'monthly_next':
        return monthlyNextRecords;
      case 'yearly':
        return yearlyRecords;
      case 'yearly_next':
        return yearlyNextRecords;
      default:
        return dailyRecords;
    }
  }, [
    activeAutoTab,
    dailyRecords,
    tomorrowRecords,
    weeklyRecords,
    weeklyNextRecords,
    monthlyRecords,
    monthlyNextRecords,
    yearlyRecords,
    yearlyNextRecords,
  ]);

  const columns = [
    { key: 'id', label: 'ID', width: '50px' },
    { key: 'sign', label: 'SIGN' },
    { key: 'vertical', label: 'VERTICAL' },
    { key: 'period', label: 'PERIOD' },
    { key: 'period_start', label: 'START' },
    { key: 'period_end', label: 'END' },
    { key: 'published_at', label: 'PUBLISHED' },
    { key: 'created_at', label: 'CREATED' },
    { key: 'updated_at', label: 'UPDATED' },
    { key: 'author', label: 'AUTHOR', width: '160px' },
    { key: 'update', label: 'UPDATE', width: '120px' },
  ] as const;

  type ColumnKey = (typeof columns)[number]['key'];

  const daysBadge = (dateString: string | null) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    const nowMid = new Date();
    nowMid.setHours(0, 0, 0, 0);
    const dMid = new Date(d);
    dMid.setHours(0, 0, 0, 0);
    const diffDays = Math.round((nowMid.getTime() - dMid.getTime()) / (1000 * 60 * 60 * 24));

    let label = '';
    if (diffDays === 0) label = 'today';
    else if (diffDays === 1) label = 'yesterday';
    else label = `${diffDays} days ago`;

    const base =
      diffDays === 0
        ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700'
        : diffDays <= 3
        ? 'bg-yellow-900/40 text-yellow-300 border-yellow-700'
        : 'bg-gray-800 text-gray-300 border-gray-700';

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono border ${base}`}>
        {label}
      </span>
    );
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    if (selectedIds.size === currentRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentRecords.map((r) => r.id)));
    }
  };

  const autoTabs: { id: AutoTab; label: string }[] = [
    { id: 'daily', label: 'Daily (today)' },
    { id: 'tomorrow', label: 'Tomorrow (next day)' },
    { id: 'weekly', label: 'Weekly (this week)' },
    { id: 'weekly_next', label: 'Weekly (next week)' },
    { id: 'monthly', label: 'Monthly (this month)' },
    { id: 'monthly_next', label: 'Monthly (next month)' },
    { id: 'yearly', label: 'Yearly (this year)' },
    { id: 'yearly_next', label: 'Yearly (next year)' },
  ];

  const fetchFor = async (tab: AutoTab, force = false) => {
    if (!currentUser) return;
    if (!force && loadedTabs.has(tab)) return;
    try {
      setLoading(true);
      const effectivePeriod =
        tab === 'tomorrow'
          ? 'daily'
          : tab === 'weekly_next'
            ? 'weekly'
            : tab === 'monthly_next'
              ? 'monthly'
              : tab === 'yearly_next'
                ? 'yearly'
                : tab;

      const params = new URLSearchParams({ period: effectivePeriod });

      if (tab === 'weekly_next' || tab === 'monthly_next' || tab === 'yearly_next') {
        params.set('window', 'next');
      }

      if (tab === 'daily') {
        params.set('date', todayStr);
      } else if (tab === 'tomorrow') {
        params.set('date', tomorrowStr);
      }
      const res = await fetch(`/api/cms/horoscope-auto?${params.toString()}`, {
        headers: { 'x-admin-phone': currentUser.phone },
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data: Horoscope[] = await res.json();
      setRecords((prev) => ({ ...prev, [tab]: data }));
      setLoadedTabs((prev) => {
        const next = new Set(prev);
        next.add(tab);
        return next;
      });
    } catch {
      // ignore for now, UI will just show empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFor('daily');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadExperts = async () => {
      if (!currentUser || expertsLoaded) return;
      try {
        const res = await fetch('/api/cms/experts', {
          headers: { 'x-admin-phone': currentUser.phone },
        });
        if (!res.ok) return;
        const data: Expert[] = await res.json();
        setExperts(data);
        setExpertsLoaded(true);
      } catch {
        // ignore
      }
    };
    loadExperts();
  }, [currentUser, expertsLoaded]);

  const getPublicUrl = (h: Horoscope) => {
    if (!h.period_start) return undefined;
    const signConfig = ZODIAC_SIGNS.find(
      (s) => s.name.toLowerCase() === String(h.sign || '').toLowerCase()
    );
    if (!signConfig) return undefined;
    const dateStr = h.period_start.slice(0, 10);
    return `/horoskop-${signConfig.slug}-${dateStr}`;
  };

  const getAstrologers = () =>
    experts.filter((exp) => {
      try {
        const arr = JSON.parse(exp.field || '[]') as string[];
        return arr.some((s) => s.toLowerCase().includes('astrologia'));
      } catch {
        return false;
      }
    });

  const getBasePromptForRecord = (h: Horoscope) => {
    const signPart = h.sign || '';
    const dateInfo = h.period_start
      ? new Date(h.period_start).toLocaleDateString('pl-PL')
      : '';
    const periodKind = periodKindPl(h.period);
    const periodDateLine = getPeriodDateLineForPrompt(h);
    return [
      `Jesteś redaktorem astrologicznym odpowiedzialnym za aktualizację już opublikowanego horoskopu.`,
      `Twoje zadanie: zaktualizuj istniejący horoskop, wykonując lekką redakcję treści bez zmiany układu HTML.`,
      ``,
      `ISTNIEJĄCY HOROSKOP`,
      `znak: ${signPart}`,
      `rodzaj okresu horoskopu: ${periodKind}`,
      `data okresu: ${periodDateLine}`,
      ``,
        `DANE`,
        `Imię i nazwisko autora pierwotnego: <EXPERT_NAME>`,
        `Styl autora pierwotnego: <EXPERT_STYLE>`,
        `\nImię i nazwisko redaktora aktualizującego: <UPDATED_BY_NAME>`,
        `Styl redaktora aktualizującego: <UPDATED_BY_STYLE>`,
        `\nData publikacji pierwotnej: <PUBLISHED_DATE>`,
        `Data aktualizacji: ${dateInfo}`,
        `\nStary kod HTML horoskopu:`,
        `<OLD_CODE>`,
        `\nCEL AKTUALIZACJI`,
        `1. Zachowaj sens, ton, strukturę i ogólną długość tekstu.`,
        `2. Wprowadź tylko lekkie zmiany redakcyjne.`,
        `3. Popraw styl, płynność, naturalność języka i interpunkcję.`,
        `4. Usuń powtórzenia i doprecyzuj maksymalnie 1-2 fragmenty.`,
        `5. Możesz dodać maksymalnie 1-2 krótkie zdania jeśli realnie poprawia to przekaz.`,
        `6. Nie pisz horoskopu od nowa.`,
        `7. Nie zmieniaj objętości sekcji w zauważalny sposób.`,
        `8. Sprawdź czy pierwsza wersja opisuje dokładnie wspomniany rok horoskopu. Zmodyfikuj treść w ten sposób aby jasno opisywał nadchodzący rok a nie teraźniejsze odczucia. Nie można używać sformułowań "w nadchodzących miesiącach" bo piszesz o aktualnym lub przyszłym roku, więc tekst ma być ogólny dla całego roku.`,
        `9. Sprawdź czy rok w tekście jest pisany liczbowo a nie słownie.`,
        `10. Jeśli piszesz o dacie przyszłej to cały tekst ma być pisany w czasie przyszłym. Np. saturn będzie sprzyjał zamiast saturn sprzyja.`,
        `\nOGRANICZENIE ZMIAN`,
        `1. W każdym akapicie zmień maksymalnie jedno zdanie lub niewielki fragment zdania.`,
        `2. Nie przepisuj całych akapitów.`,
        `3. Jeśli zdanie jest poprawne stylistycznie, pozostaw je bez zmian.`,
        `4. Zmiany mają wyglądać jak lekka redakcja, nie nowe napisanie tekstu.`,
        `\nNARRACJA`,
        `1. Nie zmieniaj sposobu narracji tekstu.`,
        `2. Jeśli tekst jest bezosobowy lub skierowany do czytelnika, zachowaj tę formę.`,
        `3. Nie wprowadzaj narracji w pierwszej osobie autora (np. piszę, patrzę, zaczynam, analizuję).`,
        `4. Nie dodawaj zwrotów typu: cześć, hej, opowiem, piszę do ciebie.`,
        `\nKRYTYCZNE ZASADY HTML`,
        `1. Zachowaj dokładnie tę samą strukturę HTML.`,
        `2. Nie wolno dodawać sekcji, usuwać sekcji, zmieniać nazw klas, zmieniać kolejności elementów ani tagów HTML.`,
        `3. Nie zmieniaj układu calloutów ani list.`,
        `4. Edytować wolno wyłącznie tekst znajdujący się wewnątrz istniejących elementów.`,
        `5. Zachowaj wszystkie istniejące elementy typograficzne takie jak nagłówki, akapity, boxy i listy.`,
        `6. Nie zmieniaj nagłówków sekcji: Ogólny, Miłosny, Praca i Finanse, Zdrowie.`,
        `\nZASADY REDAKCJI`,
        `1. Styl ma pozostać lekki, czytelny i naturalny.`,
        `2. Unikaj sztucznego lub patetycznego języka.`,
        `3. Nie używaj fraz: Wszechświat sprzyja, energia będzie intensywna, to idealny czas, wibracje, manifestacja.`,
        `4. Astrologia ma pozostać tłem, nie wykładem.`,
        `5. Jeśli pojawia się planeta, musi mieć praktyczny skutek.`,
        `6. Zachowaj codzienny, konkretny charakter tekstu.`,
        `\nWAŻNE`,
        `To jest aktualizacja istniejącego artykułu, a nie generowanie nowego horoskopu.`,
        `Efekt ma wyglądać jak lekko zredagowana wersja tego samego tekstu.`,
        `Nie pokazuj procesu myślenia ani analizy.`,
        `Nie twórz sekcji typu: analysis, reasoning, revision, explanation, self-check.`,
        `Nie pokazuj przykładów zmian typu "oryginał -> poprawiona wersja" ani "before / after".`,
        `Nie wypisuj list zmian ani komentarzy.`,
        `\nKONTROLA PRZED ZWROTEM`,
        `Sprawdź czy struktura HTML pozostała identyczna.`,
        `Sprawdź czy narracja nie została zmieniona na pierwszą osobę.`,
        `Sprawdź czy nie przepisano całych akapitów.`,
        `Sprawdź czy nie zmieniono klas CSS ani atrybutów HTML.`,
        `Sprawdź czy odpowiedź zawiera wyłącznie kod HTML.`,
        `Sprawdź czy odpowiedź zaczyna się dokładnie od znaku "<div".`,
        `Jeśli którykolwiek warunek nie jest spełniony, popraw odpowiedź przed zwróceniem.`,
        `\nWYJŚCIE`,
        `Zwróć wyłącznie gotowy HTML horoskopu.`,
        `Odpowiedź musi zaczynać się od "<div" i nie może zawierać żadnego tekstu poza HTML.`,
        `Nie używaj markdown ani bloków kodu.`
      ].join('\n');
  };

  const openEdit = (h: Horoscope) => {
    setEditing(h);
    const stored = generatedByRowId[h.id];
    setLeftText(h.text || '');
    setRightText(stored ? stored.text : (h.text || ''));
    try {
      const astrologers = getAstrologers();
      if (astrologers.length > 0) {
        const random = astrologers[Math.floor(Math.random() * astrologers.length)];
        setSelectedExpertId(stored?.expertId ?? (random.user_id || random.rowid));
      } else {
        setSelectedExpertId(stored?.expertId ?? null);
      }
    } catch {
      setSelectedExpertId(stored?.expertId ?? null);
    }
    setGenError(null);
    const basePrompt = getBasePromptForRecord(h);
    setPromptText(basePrompt);
  };

  const closeEdit = () => {
    setEditing(null);
    setSaving(false);
    setGenLoading(false);
    setGenError(null);
    setShowPreview(false);
  };

  const handlePublish = async () => {
    if (!currentUser || !editing) return;
    setSaving(true);
    try {
      // merge author_id: add selectedExpertId if chosen
      let authorJson: string | null = null;
      try {
        const existing = (editing as any).author_id;
        const arr = existing ? JSON.parse(existing) : [];
        const list = Array.isArray(arr) ? [...arr] : [];
        if (selectedExpertId != null) {
          const idStr = String(selectedExpertId);
          if (!list.includes(idStr)) list.push(idStr);
        }
        authorJson = list.length > 0 ? JSON.stringify(list) : null;
      } catch {
        authorJson =
          selectedExpertId != null ? JSON.stringify([String(selectedExpertId)]) : null;
      }

      const payload = {
        id: editing.id,
        sign: editing.sign,
        vertical: editing.vertical,
        period: editing.period,
        period_start: editing.period_start,
        period_end: editing.period_end,
        text: rightText,
        published_at: editing.published_at,
        author_id: authorJson,
      };

      const res = await fetch('/api/cms/horoscope', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-phone': currentUser.phone,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setSaving(false);
        return;
      }

      // po zapisie z modala usuwamy ewentualne wygenerowane dane dla tego rekordu,
      // żeby dodatkowe ikonki w tabeli zniknęły
      setGeneratedByRowId((prev) => {
        if (!editing) return prev;
        const next = { ...prev };
        delete next[editing.id];
        return next;
      });

      // po zapisie po prostu odśwież bieżący okres
      await fetchFor(activeAutoTab, true);
      closeEdit();
    } catch {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!editing) return;
    setGenError(null);
    setGenLoading(true);
    try {
      const expert =
        selectedExpertId != null
          ? experts.find(
              (e) => e.user_id === selectedExpertId || e.rowid === selectedExpertId
            )
          : null;

      // Stary autor z pierwszego wpisu w authors (jeśli dostępny)
      const originalAuthor = (editing.authors && editing.authors[0]) || null;
      const updatedByName = expert?.name || currentUser?.name || '';
      const updatedByStyle = expert?.style || '';
      const publishedDate = editing.published_at
        ? new Date(editing.published_at).toLocaleDateString('pl-PL')
        : '';

      let finalPrompt = promptText;
      finalPrompt = finalPrompt.replace(/<EXPERT_NAME>/g, originalAuthor?.name || '');
      finalPrompt = finalPrompt.replace(/<EXPERT_STYLE>/g, originalAuthor?.style || '');
      finalPrompt = finalPrompt.replace(/<UPDATED_BY_NAME>/g, updatedByName);
      finalPrompt = finalPrompt.replace(/<UPDATED_BY_STYLE>/g, updatedByStyle);
      finalPrompt = finalPrompt.replace(/<PUBLISHED_DATE>/g, publishedDate);
      finalPrompt = finalPrompt.replace(/<OLD_CODE>/g, leftText || '');

      const res = await fetch('/api/cms/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'horoscope_yearly', prompt: finalPrompt }),
      });
      if (!res.ok) {
        let msg = 'AI request failed';
        try {
          const data = await res.json();
          if (data?.error) msg = String(data.error);
        } catch {
          // ignore
        }
        setGenError(msg);
        setGenLoading(false);
        return;
      }
      const data = await res.json();
      if (!data || typeof data.text !== 'string' || !data.text.trim()) {
        setGenError('AI returned empty or invalid text.');
        setGenLoading(false);
        return;
      }
      setRightText(data.text);
      setGenLoading(false);
      setPromptPreview(null);
    } catch (e: any) {
      setGenError(
        e instanceof Error ? e.message : 'Unexpected error while calling AI.'
      );
      setGenLoading(false);
    }
  };

  const buildFinalPrompt = (
    record: Horoscope,
    expert: Expert | null,
    basePrompt: string,
    oldCode: string
  ) => {
    const originalAuthor = (record.authors && record.authors[0]) || null;
    const updatedByName = expert?.name || currentUser?.name || '';
    const updatedByStyle = expert?.style || '';
    const publishedDate = record.published_at
      ? new Date(record.published_at).toLocaleDateString('pl-PL')
      : '';
    let fp = basePrompt;
    fp = fp.replace(/<EXPERT_NAME>/g, originalAuthor?.name || '');
    fp = fp.replace(/<EXPERT_STYLE>/g, originalAuthor?.style || '');
    fp = fp.replace(/<UPDATED_BY_NAME>/g, updatedByName);
    fp = fp.replace(/<UPDATED_BY_STYLE>/g, updatedByStyle);
    fp = fp.replace(/<PUBLISHED_DATE>/g, publishedDate);
    fp = fp.replace(/<OLD_CODE>/g, oldCode);
    return fp;
  };

  const runBatchUpdate = async () => {
    const list = currentRecords.filter((r) => selectedIds.has(r.id));
    if (list.length === 0) return;
    setBatchGenerating(true);
    setBatchTotal(list.length);
    setBatchCurrentId(null);
    const astrologers = getAstrologers();
    for (let i = 0; i < list.length; i++) {
      setBatchCurrentIndex(i);
      setBatchCurrentId(list[i].id);
      const record = list[i];
      const expert =
        astrologers.length > 0
          ? astrologers[Math.floor(Math.random() * astrologers.length)]
          : null;
      const expertId = expert?.user_id ?? expert?.rowid ?? null;
      const basePrompt = getBasePromptForRecord(record);
      const finalPrompt = buildFinalPrompt(
        record,
        expert,
        basePrompt,
        record.text || ''
      );
      try {
        const res = await fetch('/api/cms/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'horoscope_yearly', prompt: finalPrompt }),
        });
        if (!res.ok) continue;
        const data = await res.json();
        if (data?.text && typeof data.text === 'string' && data.text.trim()) {
          setGeneratedByRowId((prev) => ({
            ...prev,
            [record.id]: { text: data.text.trim(), expertId },
          }));
        }
      } catch {
        // skip failed row
      }
    }
    setBatchGenerating(false);
    setBatchCurrentIndex(0);
    setBatchCurrentId(null);
  };

  const handlePublishRow = async (item: Horoscope) => {
    const stored = generatedByRowId[item.id];
    if (!currentUser || !stored) return;
    setSavingRowId(item.id);
    try {
      let authorJson: string | null = null;
      try {
        const existing = (item as any).author_id;
        const arr = existing ? JSON.parse(existing) : [];
        const list = Array.isArray(arr) ? [...arr] : [];
        if (stored.expertId != null) {
          const idStr = String(stored.expertId);
          if (!list.includes(idStr)) list.push(idStr);
        }
        authorJson = list.length > 0 ? JSON.stringify(list) : null;
      } catch {
        authorJson =
          stored.expertId != null ? JSON.stringify([String(stored.expertId)]) : null;
      }
      const payload = {
        id: item.id,
        sign: item.sign,
        vertical: item.vertical,
        period: item.period,
        period_start: item.period_start,
        period_end: item.period_end,
        text: stored.text,
        published_at: item.published_at,
        author_id: authorJson,
      };
      const res = await fetch('/api/cms/horoscope', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-phone': currentUser.phone,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setGeneratedByRowId((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
        await fetchFor(activeAutoTab, true);
      }
    } finally {
      setSavingRowId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Local tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {autoTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveAutoTab(tab.id);
                setSelectedIds(new Set());
                fetchFor(tab.id);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                activeAutoTab === tab.id
                  ? 'bg-mystic-accent text-black'
                  : 'bg-mystic-box text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => fetchFor(activeAutoTab, true)}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-mystic-box border border-gray-700 text-gray-300 hover:text-white hover:border-mystic-accent"
        >
          <RefreshCw className="w-3 h-3" />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Table header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Sun className="w-5 h-5" /> Auto Updater - {autoTabs.find((t) => t.id === activeAutoTab)?.label}{' '}
          ({currentRecords.length})
        </h2>
      </div>

      {/* Table same style as YearlyTab */}
      <div className="overflow-x-auto border border-gray-800 rounded-sm">
        <table className="w-full text-left border-collapse table-fixed">
          <thead className="bg-mystic-box">
            <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
              <th className="p-2 border-r border-gray-800 w-10 text-center sticky left-0 bg-mystic-box">
                <div onClick={handleSelectAllVisible} className="cursor-pointer flex justify-center">
                  {currentRecords.length > 0 && selectedIds.size === currentRecords.length ? (
                    <CheckSquare className="w-4 h-4 text-blue-400" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </div>
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="p-2 border-r border-gray-800 last:border-r-0"
                  style={{ minWidth: (col as any).width || '120px' }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{col.label}</span>
                    {col.key === 'update' && (
                      <>
                        <button
                          type="button"
                          onClick={runBatchUpdate}
                          disabled={batchGenerating || selectedIds.size === 0}
                          className="px-2 py-1 text-[10px] rounded-full bg-mystic-accent/80 text-black font-bold hover:bg-mystic-accent disabled:opacity-50"
                        >
                          Update
                        </button>
                        {batchGenerating && batchTotal > 0 && (
                          <span className="text-[10px] text-gray-400 font-mono">
                            {batchCurrentIndex + 1} / {batchTotal}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800 bg-mystic-bg">
            {currentRecords.map((item) => (
              <tr
                key={item.id}
                className={`group hover:bg-mystic-btn ${selectedIds.has(item.id) ? 'bg-blue-900/10' : ''}`}
              >
                <td className="p-2 border-r border-gray-800 text-center sticky left-0 bg-mystic-bg group-hover:bg-mystic-btn">
                  <div onClick={() => handleSelectRow(item.id)} className="cursor-pointer flex justify-center">
                    {selectedIds.has(item.id) ? (
                      <CheckSquare className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />
                    )}
                  </div>
                </td>
                {columns.map((col) => {
                  const key = col.key as ColumnKey;
                  const value = (item as any)[key] as unknown;
                  let displayValue: React.ReactNode = typeof value === 'string' ? value : value;

                  if (key === 'id') {
                    const url = getPublicUrl(item);
                    displayValue = url ? (
                      <a
                        href={url}
                        className="font-bold text-mystic-accent hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.id}
                      </a>
                    ) : (
                      item.id
                    );
                  } else if (key === 'period_start' || key === 'period_end') {
                    displayValue = formatDate(value as string | null);
                  } else if (key === 'published_at' || key === 'created_at' || key === 'updated_at') {
                    displayValue = (
                      <div className="flex flex-col gap-1">
                        {daysBadge(value as string | null)}
                        <span className="text-[11px] text-gray-300">{formatDate(value as string | null)}</span>
                      </div>
                    );
                  } else if (key === 'author') {
                    const authors = item.authors || [];
                    displayValue =
                      authors.length === 0 ? (
                        <span className="text-[11px] text-gray-500 italic">Brak</span>
                      ) : (
                        <div className="flex -space-x-2">
                          {authors.map((a) => (
                            <a
                              key={a.id}
                              href={a.slug ? `/${a.slug}` : undefined}
                              className="block"
                              title={a.name}
                            >
                              <img
                                src={a.avatar || 'https://assets.esoteric.pl/img/default-avatar.png'}
                                alt={a.name}
                                className="w-7 h-7 rounded-full border border-gray-800 object-cover"
                              />
                            </a>
                          ))}
                        </div>
                      );
                  } else if (key === 'update') {
                    const isCurrentBatch = batchGenerating && batchCurrentId === item.id;
                    const generated = generatedByRowId[item.id];
                    const saving = savingRowId === item.id;
                    displayValue = (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {isCurrentBatch && (
                          <Loader2 className="w-4 h-4 text-mystic-accent animate-spin shrink-0" />
                        )}
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="px-3 py-1 text-[11px] rounded-full bg-mystic-accent text-black font-bold hover:bg-mystic-accentHover shrink-0"
                        >
                          Update
                        </button>
                        {generated && (
                          <>
                            <button
                              type="button"
                              onClick={() => setHtmlPreviewRowId(item.id)}
                              className="p-1.5 rounded-full bg-mystic-btn text-gray-300 hover:text-white hover:bg-[#333] border border-gray-700"
                              title="Podgląd zmian (HTML)"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setCodePreviewRowId(item.id)}
                              className="p-1.5 rounded-full bg-mystic-btn text-gray-300 hover:text-white hover:bg-[#333] border border-gray-700"
                              title="Podgląd kodu (stary vs nowy)"
                            >
                              <Code className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePublishRow(item)}
                              disabled={saving}
                              className="p-1.5 rounded-full bg-mystic-btn text-gray-300 hover:text-white hover:bg-[#333] border border-gray-700 disabled:opacity-50"
                              title="Opublikuj"
                            >
                              {saving ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Save className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  }

                  return (
                    <td
                      key={col.key}
                      className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300"
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
            {currentRecords.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="p-4 text-center text-xs text-gray-500">
                  Brak horoskopów dla wybranego okresu (na podstawie bieżącej daty).
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-mystic-box border border-gray-800 w-full max-w-6xl max-h-[95vh] rounded-xl shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <div>
                <h3 className="text-sm font-bold text-white">
                  Update horoscope - {editing.sign} ({editing.period_start})
                </h3>
                <p className="text-[11px] text-gray-500">
                  Najpierw doprecyzuj prompt i wybierz eksperta, potem zaktualizuj tekst po prawej
                  stronie i opublikuj.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                className="p-1.5 rounded-full bg-black/40 text-gray-400 hover:text-white hover:bg-black/70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Wiersz 1: prompt + eksperci */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 mb-1 uppercase">
                    Prompt dla AI
                  </label>
                  <textarea
                    className="w-full h-32 bg-[#111111] border border-gray-700 rounded-md text-xs text-gray-100 p-2 font-mono resize-none"
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                  />
                  <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-gray-500">
                    <span>Dostępne znaczniki:</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;EXPERT_STYLE&gt;</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;EXPERT_NAME&gt;</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;OLD_CODE&gt;</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;UPDATED_BY_NAME&gt;</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;UPDATED_BY_STYLE&gt;</span>
                    <span className="px-1.5 py-0.5 rounded bg-mystic-btn text-gray-200 font-mono">&lt;PUBLISHED_DATE&gt;</span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 mb-1 uppercase">
                    Wybierz eksperta-aktualizatora
                  </label>
                  <div className="bg-[#111111] border border-gray-700 rounded-md p-2 max-h-32 overflow-auto space-y-1">
                    {experts.length === 0 && (
                      <p className="text-[11px] text-gray-500 italic">
                        Brak ekspertów lub nie udało się ich załadować.
                      </p>
                    )}
                    {getAstrologers().map((exp) => (
                      <label
                        key={exp.rowid}
                        className="flex items-start gap-2 text-[11px] text-gray-200 cursor-pointer hover:bg-white/5 rounded px-1 py-0.5"
                      >
                        <input
                          type="radio"
                          name="update-expert"
                          className="accent-mystic-accent"
                          checked={selectedExpertId === exp.user_id}
                          onChange={() => setSelectedExpertId(exp.user_id || exp.rowid)}
                        />
                        <span className="flex flex-col">
                          <span>{exp.name}</span>
                          <span className="text-[10px] text-gray-400">
                            {(() => {
                              try {
                                const arr = JSON.parse(exp.field || '[]') as string[];
                                return arr.join(', ');
                              } catch {
                                return '';
                              }
                            })()}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="pt-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={genLoading}
                      className="px-3 py-1 rounded-full text-[11px] font-bold bg-mystic-accent text-black hover:bg-mystic-accentHover disabled:opacity-60"
                    >
                      {genLoading ? 'Generating…' : 'Generate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!editing) return;
                        const originalAuthor =
                          (editing.authors && editing.authors[0]) || null;
                        const expertLocal =
                          selectedExpertId != null
                            ? experts.find(
                                (e) =>
                                  e.user_id === selectedExpertId ||
                                  e.rowid === selectedExpertId
                              )
                            : null;
                        const updatedByName =
                          expertLocal?.name || currentUser?.name || '';
                        const updatedByStyle = expertLocal?.style || '';
                        const publishedDate = editing.published_at
                          ? new Date(editing.published_at).toLocaleDateString('pl-PL')
                          : '';

                        let fp = promptText;
                        fp = fp.replace(/<EXPERT_NAME>/g, originalAuthor?.name || '');
                        fp = fp.replace(/<EXPERT_STYLE>/g, originalAuthor?.style || '');
                        fp = fp.replace(/<UPDATED_BY_NAME>/g, updatedByName);
                        fp = fp.replace(/<UPDATED_BY_STYLE>/g, updatedByStyle);
                        fp = fp.replace(/<PUBLISHED_DATE>/g, publishedDate);
                        fp = fp.replace(/<OLD_CODE>/g, leftText || '');
                        setPromptPreview(fp);
                      }}
                      className="px-3 py-1 rounded-full text-[11px] bg-[#1f2937] text-gray-100 border border-gray-600 hover:bg-[#111827]"
                    >
                      View prompt
                    </button>
                    {genError && (
                      <span className="text-[10px] text-red-400 text-right">
                        {genError}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Wiersz 2: dwa edytory */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 mb-1 uppercase">
                    Stara wersja
                  </label>
                  <div className="w-full h-64 bg-[#111111] border border-gray-700 rounded-md overflow-hidden">
                    <CodeMirror
                      value={leftText}
                      height="100%"
                      theme="dark"
                      extensions={[html()]}
                      editable={false}
                      basicSetup={{ lineNumbers: true, foldGutter: true }}
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-bold text-gray-400 mb-1 uppercase">
                    Nowa wersja (wklej / edytuj tekst z AI)
                  </label>
                  <div className="w-full h-64 bg-[#111111] border border-gray-700 rounded-md overflow-hidden">
                    <CodeMirror
                      value={rightText}
                      height="100%"
                      theme="dark"
                      extensions={[html()]}
                      basicSetup={{ lineNumbers: true, foldGutter: true }}
                      onChange={(value) => setRightText(value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800 bg-[#111111]">
              <p className="text-[11px] text-gray-500">
                Opublikowanie nadpisze treść horoskopu i zaktualizuje datę <span className="font-bold">Updated</span>.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-3 py-1.5 rounded-full text-[11px] bg-gray-700 text-gray-100 hover:bg-gray-600"
                  disabled={saving}
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="px-3 py-1.5 rounded-full text-[11px] bg-[#1f2937] text-gray-100 border border-gray-600 hover:bg-[#111827]"
                  disabled={saving}
                >
                  Podgląd
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={saving}
                  className="px-4 py-1.5 rounded-full text-[11px] font-bold bg-mystic-accent text-black hover:bg-mystic-accentHover disabled:opacity-60"
                >
                  {saving ? 'Zapisywanie…' : 'Opublikuj'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && showPreview && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111111] border border-gray-800 w-full max-w-6xl max-h-[95vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <div>
                <h3 className="text-sm font-bold text-white">
                  Podgląd horoskopu - {editing.sign} ({editing.period_start})
                </h3>
                <p className="text-[11px] text-gray-500">
                  Po lewej stara wersja, po prawej nowa wersja. To finalny wygląd HTML, bez kodu.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="p-1.5 rounded-full bg-black/40 text-gray-400 hover:text-white hover:bg-black/70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-auto">
              <div className="border-r border-gray-800 p-4 space-y-2">
                <div className="text-[11px] font-bold text-gray-400 uppercase">
                  Stara wersja
                </div>
                <div
                  className="prose prose-invert max-w-none text-gray-200 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: leftText }}
                />
              </div>
              <div className="p-4 space-y-2">
                <div className="text-[11px] font-bold text-gray-400 uppercase">
                  Nowa wersja
                </div>
                <div
                  className="prose prose-invert max-w-none text-gray-200 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: highlightDiffHtml(leftText, rightText) }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {promptPreview && (
        <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-mystic-box border border-gray-700 w-full max-w-3xl max-h-[90vh] rounded-xl flex flex-col shadow-2xl">
            <div className="p-4 border-b border-gray-800 flex justify-between items-center">
              <h3 className="font-bold text-white text-sm">Final AI prompt</h3>
              <button
                type="button"
                onClick={() => setPromptPreview(null)}
                className="p-1 text-gray-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto">
              <pre className="whitespace-pre-wrap text-[11px] text-gray-200 font-mono">
                {promptPreview}
              </pre>
            </div>
          </div>
        </div>
      )}

      {htmlPreviewRowId != null && (() => {
        const record = currentRecords.find((r) => r.id === htmlPreviewRowId);
        const gen = record ? generatedByRowId[record.id] : null;
        if (!record || !gen) return null;
        return (
          <div className="fixed inset-0 z-[112] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-[#111111] border border-gray-800 w-full max-w-6xl max-h-[95vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Podgląd zmian - {record.sign} ({record.period_start})
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Po lewej stara wersja, po prawej nowa wersja z zaznaczonymi zmianami.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setHtmlPreviewRowId(null)}
                  className="p-1.5 rounded-full bg-black/40 text-gray-400 hover:text-white hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-auto">
                <div className="border-r border-gray-800 p-4 space-y-2">
                  <div className="text-[11px] font-bold text-gray-400 uppercase">
                    Stara wersja
                  </div>
                  <div
                    className="prose prose-invert max-w-none text-gray-200 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: record.text || '' }}
                  />
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-[11px] font-bold text-gray-400 uppercase">
                    Nowa wersja
                  </div>
                  <div
                    className="prose prose-invert max-w-none text-gray-200 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: highlightDiffHtml(record.text || '', gen.text),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {codePreviewRowId != null && (() => {
        const record = currentRecords.find((r) => r.id === codePreviewRowId);
        const gen = record ? generatedByRowId[record.id] : null;
        if (!record || !gen) return null;
        return (
          <div className="fixed inset-0 z-[115] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-mystic-box border border-gray-800 w-full max-w-6xl max-h-[95vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                <h3 className="text-sm font-bold text-white">
                  Podgląd kodu - {record.sign} ({record.period_start})
                </h3>
                <button
                  type="button"
                  onClick={() => setCodePreviewRowId(null)}
                  className="p-1.5 rounded-full bg-black/40 text-gray-400 hover:text-white hover:bg-black/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-auto min-h-0">
                <div className="flex flex-col border-r border-gray-800 p-2">
                  <div className="text-[11px] font-bold text-gray-400 uppercase mb-1">Stary kod</div>
                  <div className="flex-1 min-h-[200px] bg-[#111111] border border-gray-700 rounded-md overflow-hidden">
                    <CodeMirror
                      value={record.text || ''}
                      height="100%"
                      minHeight="200px"
                      theme="dark"
                      extensions={[html()]}
                      editable={false}
                      basicSetup={{ lineNumbers: true, foldGutter: true }}
                    />
                  </div>
                </div>
                <div className="flex flex-col p-2">
                  <div className="text-[11px] font-bold text-gray-400 uppercase mb-1">Nowy kod</div>
                  <div className="flex-1 min-h-[200px] bg-[#111111] border border-gray-700 rounded-md overflow-hidden">
                    <CodeMirror
                      value={gen.text}
                      height="100%"
                      minHeight="200px"
                      theme="dark"
                      extensions={[html()]}
                      editable={false}
                      basicSetup={{ lineNumbers: true, foldGutter: true }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AutoUpdaterTab;

